import React from 'react';
import {
  APPROVAL_FLOW_STEPS,
  ApprovalFlowStage
} from '../../../utils/approvalFlow';
import './ApprovalFlowSteps.css';

export interface ApprovalFlowStepsProps {
  /**
   * The currently active stage. Steps before it are rendered as completed
   * (filled), the active step and any later steps are rendered as upcoming.
   */
  activeStage: ApprovalFlowStage;
}

/**
 * Reusable 4-step progress indicator for the Async approval flow:
 * Add/Edit → Send for Approval → Pending → Approved.
 *
 * Purely presentational: the parent decides whether to render it and which
 * stage is active (see `computeApprovalFlowStage` in utils/approvalFlow).
 */
const ApprovalFlowSteps: React.FC<ApprovalFlowStepsProps> = ({
  activeStage
}) => {
  return (
    <div
      className="approval-flow-steps"
      role="list"
      aria-label="Approval progress"
      data-testid="approval-flow-steps"
    >
      {APPROVAL_FLOW_STEPS.map((step, index) => {
        const isCompleted = step.stage < activeStage;
        const isCurrent = step.stage === activeStage;
        const stepClass = isCurrent
          ? 'is-current'
          : isCompleted
          ? 'is-completed'
          : 'is-upcoming';

        // The connector after this step is filled once the NEXT step has been
        // reached (completed or current), so the bar fills up to the current step.
        const nextStep = APPROVAL_FLOW_STEPS[index + 1];
        const trackFilled = !!nextStep && nextStep.stage <= activeStage;

        return (
          <React.Fragment key={step.stage}>
            <div
              className={`approval-flow-step ${stepClass}`}
              role="listitem"
              aria-current={isCurrent ? 'step' : undefined}
            >
              <span className="approval-flow-step-label">{step.label}</span>
              <span className="approval-flow-step-icon">
                {isCompleted ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="8"
                    height="8"
                    viewBox="0 0 8 8"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M2.7603 7.20082C2.52046 7.20082 2.29311 7.093 2.14077 6.90707L0.180609 4.50707C-0.0990793 4.16488 -0.0482953 3.66097 0.293889 3.38128C0.636073 3.10081 1.14076 3.15238 1.41967 3.49456L2.74546 5.118L6.4892 0.309411C6.7603 -0.0390295 7.26264 -0.10387 7.61186 0.16957C7.9603 0.440666 8.02279 0.943786 7.7517 1.29223L3.39154 6.89223C3.24232 7.08441 3.01341 7.1977 2.76966 7.20082H2.7603Z"
                      fill="#fff"
                    />
                  </svg>
                ) : (
                  step.stage
                )}
              </span>
            </div>
            {nextStep && (
              <span
                className={`approval-flow-track ${
                  trackFilled ? 'is-filled' : 'is-upcoming'
                }`}
                aria-hidden="true"
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default ApprovalFlowSteps;
